import { useMemo } from 'react'

import { useAppSelector } from '~/redux/hooks'
import { permissionsItems } from '~/utils/permissions-utils'
import {
  ROLES,
  ROLES_MAP,
  canAccess,
  checkAccessToInterventions,
  checkAccessToProjects,
  checkAccessToSuperset,
} from '~/utils/role-utils'
import { type User } from '~/utils/types/user'

import { type MenuTab } from '../types/menu'
import { addSupersetTab, isSidebarItemVisible } from '../utils/menu-utils'

function getRoleTabs(user: User): MenuTab[] {
  const tabsByRole = ROLES_MAP[user.role] ?? []

  return tabsByRole
    .filter(
      ({ item, shouldShowOnHeader }) =>
        item === 'homepage' || shouldShowOnHeader,
    )
    .filter(({ item }) => isSidebarItemVisible(item))
    .filter(({ item }) => {
      if (item === 'projects') {
        return (
          user.role === ROLES.ORGANIZATION_ADMIN && checkAccessToProjects(user)
        )
      }
      if (item === 'solutions') return canAccess(user, 'solutions')
      if (item === 'interventions') return checkAccessToInterventions(user)

      return true
    })
    .filter(({ item }) => {
      if (
        item === 'solutions' &&
        (user.role === ROLES.ADMIN || user.role === ROLES.PS_TEAM)
      ) {
        return !user.permissions?.includes('solutions_read')
      }

      const hasUserPermissions =
        !!user.permissions && user.permissions.length > 1
      if (!hasUserPermissions) return true

      return !permissionsItems.some(
        permissionItem => permissionItem.item === item,
      )
    })
    .map(tab => {
      if (tab.options?.includes('superset') && !checkAccessToSuperset(user)) {
        return { ...tab, options: tab.options.filter(o => o !== 'superset') }
      }

      return tab
    })
}

function getPermissionTabs(user: User): MenuTab[] {
  const permissions = user.permissions ?? []
  const permissionTabs: MenuTab[] = []
  const permissionsWithReadAccess = permissions
    .filter(permission => permission.split('_')[1] === 'read')
    .map(permission => permission.split('_')[0])

  permissionsItems.forEach(permissionItem => {
    permissionsWithReadAccess.forEach(permissionRead => {
      const { options, item } = permissionItem
      const hasTabOptions = !!options?.length
      const isModuleInItem = item === permissionRead
      const isModuleInOptions = options?.includes(permissionRead) ?? false

      if (!isModuleInItem && !isModuleInOptions) return

      const permissionTabIndex = permissionTabs.findIndex(
        tab => tab.item === item,
      )

      if (!hasTabOptions) {
        permissionTabs.push({
          item: permissionRead,
          shouldShowOnHeader: true,
        })
        return
      }

      if (permissionTabIndex !== -1) {
        const currentOptions = permissionTabs[permissionTabIndex].options ?? []
        permissionTabs[permissionTabIndex] = {
          shouldShowOnHeader: true,
          item,
          options: [...currentOptions, permissionRead],
        }
        return
      }

      permissionTabs.push({
        item,
        shouldShowOnHeader: true,
        options: [permissionRead],
      })
    })
  })

  return permissionTabs.filter(
    ({ item }) =>
      isSidebarItemVisible(item) &&
      (item !== 'solutions' || canAccess(user, 'solutions')),
  )
}

export function getMenuTabs(user: User | null): MenuTab[] {
  if (!user) return []

  const tabs = [...getPermissionTabs(user), ...getRoleTabs(user)]

  if (
    canAccess(user, 'solutions') &&
    !tabs.some(tab => tab.item === 'solutions')
  ) {
    tabs.push({ item: 'solutions', shouldShowOnHeader: true })
  }
  const hasHubAdminInvitationTab = tabs.some(
    tab =>
      tab.item === 'user-invitation' ||
      tab.options?.includes('user-invitation'),
  )

  if (user.role === ROLES.HUB_ADMIN && !hasHubAdminInvitationTab) {
    tabs.push({ item: 'user-invitation', shouldShowOnHeader: true })
  }

  return addSupersetTab(moveHomepageToTop(tabs, user), user)
}

// Hub Admins with custom permissions get permission tabs prepended, which
// pushes "Novedades" (homepage) down the sidebar. Keep it first.
function moveHomepageToTop(tabs: MenuTab[], user: User): MenuTab[] {
  if (user.role !== ROLES.HUB_ADMIN) return tabs

  const homepageTab = tabs.find(tab => tab.item === 'homepage')
  if (!homepageTab) return tabs

  return [homepageTab, ...tabs.filter(tab => tab !== homepageTab)]
}

export function useMenuTabs() {
  const user = useAppSelector(state => state.user)

  return useMemo(() => getMenuTabs(user), [user])
}
